import { VENUE_PHOTO_MIN_SIZE_MESSAGE } from '@bonsai/shared';

import type { PhotoSize } from './photo-crop';
import { PHOTO_HARD_CAP_BYTES, PHOTO_HARD_CAP_MESSAGE } from './photo-shrink-plan';

const BYTES_PER_MB = 1024 * 1024;

/** Exactly these three, so iPhones turn their HEIC photos into JPEG when choosing one. */
export const ACCEPT_ATTRIBUTE = 'image/jpeg,image/png,image/webp';
const ACCEPTED_TYPES = ACCEPT_ATTRIBUTE.split(',');

export const WRONG_TYPE_MESSAGE = 'Choose a JPEG, PNG or WebP photo.';
export const MIN_SIZE_MESSAGE = VENUE_PHOTO_MIN_SIZE_MESSAGE;
export const UNREADABLE_MESSAGE = "That file couldn't be read as a photo. Try a different one.";

/** Why a chosen file can be turned down before it is sent, or null when it looks fine. */
export function problemWithFile(file: File): string | null {
  if (file.type && !ACCEPTED_TYPES.includes(file.type)) return WRONG_TYPE_MESSAGE;
  if (file.size > PHOTO_HARD_CAP_BYTES) return PHOTO_HARD_CAP_MESSAGE;
  return null;
}

function formatSize(bytes: number): string {
  if (bytes < BYTES_PER_MB / 10) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / BYTES_PER_MB).toFixed(1)} MB`;
}

/** The file's name and size as separate pieces, e.g. "IMG_4471.jpg" and "14.2 MB". */
export function fileNameAndSize(file: File): { name: string; size: string } {
  return { name: file.name, size: formatSize(file.size) };
}

/** "IMG_4471.jpg · 14.2 MB" */
export function describeFile(file: File): string {
  return `${file.name} · ${formatSize(file.size)}`;
}

/**
 * The photo's size as the browser shows it. Browsers turn a phone photo upright using its
 * rotation data, and the server does the same before cropping, so the two agree. (A photo that
 * gets shrunk first is already upright with no rotation data, so it agrees too.)
 */
export function loadPhotoSize(url: string): Promise<PhotoSize> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      if (image.naturalWidth > 0 && image.naturalHeight > 0) {
        resolve({ width: image.naturalWidth, height: image.naturalHeight });
      } else {
        reject(new Error(UNREADABLE_MESSAGE));
      }
    };
    image.onerror = () => reject(new Error(UNREADABLE_MESSAGE));
    image.src = url;
  });
}
